import { Group, Select, Stack, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PageSize } from '@/utils/usePageSize';
import { PAGE_SIZE_OPTIONS } from '@/utils/usePageSize';

import { MobileSelectSheet } from './FormField';
import { PaginationButtons } from './TablePagination';

interface ListPaginationProps {
  label: string;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: PageSize;
  onPageSizeChange: (pageSize: PageSize) => void;
  bordered?: boolean;
}

// Standardizirana paginacija za sve glavne list-stranice — oznaka lijevo,
// page-size selector odmah ispred gumba za stranice, oboje desno, u istom
// retku (vidi GEN-02/GEN-03).
export const ListPagination = ({
  label,
  page,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  bordered = true
}: ListPaginationProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 48em)');
  const [sheetOpened, setSheetOpened] = useState(false);
  const sizeOptions = PAGE_SIZE_OPTIONS.map((size) => String(size));

  const wrapperStyle = bordered
    ? { padding: '14px 20px', borderTop: '1px solid var(--color-border-soft)' }
    : { padding: '8px 0', width: '100%' };

  const controls = (
    <Group gap={16} wrap="nowrap">
      <Group gap={8} wrap="nowrap">
        <Text fz={11.5} style={{ color: 'var(--color-text-muted)' }}>
          {t('pagination.perPage')}
        </Text>
        <Select
          data={sizeOptions}
          value={String(pageSize)}
          dropdownOpened={isMobile ? false : undefined}
          onDropdownOpen={() => isMobile && setSheetOpened(true)}
          onChange={(value) => value && onPageSizeChange(Number(value) as PageSize)}
          allowDeselect={false}
          w={72}
          size="xs"
          classNames={{ input: 'no-zoom-input' }}
          styles={{
            input: {
              height: 32,
              minHeight: 32,
              borderRadius: 6,
              border: '1px solid var(--color-border-default)',
              backgroundColor: 'var(--color-bg-surface)',
              color: 'var(--color-text-primary)',
              fontWeight: 600
            },
            section: { color: 'var(--color-text-muted)' },
            dropdown: {
              backgroundColor: 'var(--color-bg-surface)',
              borderColor: 'var(--color-border-default)'
            },
            option: { color: 'var(--color-text-primary)' }
          }}
          onMouseDown={(event) => event.preventDefault()}
        />
      </Group>
      <PaginationButtons page={page} totalPages={totalPages} onPageChange={onPageChange} />
    </Group>
  );

  return (
    <>
      {/* Mobile — oznaka pa kontrole u zasebnom retku, kontrole centrirane (ne lijevo poravnate). */}
      <Stack gap={10} align="center" hiddenFrom="sm" style={wrapperStyle}>
        <Text fz={11.5} ta="center" style={{ color: 'var(--color-text-muted)' }}>
          {label}
        </Text>
        <Group justify="center">{controls}</Group>
      </Stack>

      {/* Desktop — nepromijenjeno, oznaka i kontrole u istom retku. */}
      <Group justify="space-between" wrap="wrap" gap={16} visibleFrom="sm" style={wrapperStyle}>
        <Text fz={11.5} style={{ color: 'var(--color-text-muted)' }}>
          {label}
        </Text>
        {controls}
      </Group>
      {isMobile && (
        <MobileSelectSheet
          opened={sheetOpened}
          onClose={() => setSheetOpened(false)}
          title={t('pagination.perPage')}
          data={sizeOptions}
          value={String(pageSize)}
          onSelect={(item) => {
            onPageSizeChange(Number(item.value) as PageSize);
            setSheetOpened(false);
          }}
        />
      )}
    </>
  );
};
